import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = useMemo(() => params.get('token') || '', [params]);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const tooShort = password.length > 0 && password.length < 8;
    const mismatch = confirm.length > 0 && password !== confirm;
    const canSubmit = !!token && password.length >= 8 && password === confirm && !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/auth/reset-password`, {
                token,
                new_password: password,
            });
            setDone(true);
            toast.success('Mot de passe réinitialisé');
            setTimeout(() => navigate('/login', { replace: true }), 1800);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] p-6" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <Card className="border-0 shadow-xl">
                    <CardHeader className="space-y-1 pb-6">
                        <CardTitle className="text-2xl text-center text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Réinitialiser le mot de passe
                        </CardTitle>
                        <CardDescription className="text-center text-gray-500">
                            Choisissez un nouveau mot de passe pour votre compte Luxy Move.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {!token ? (
                            <div className="text-center py-6 space-y-3" data-testid="reset-no-token">
                                <p className="text-sm text-red-600">Le lien de réinitialisation est manquant ou invalide.</p>
                                <Link to="/login" className="text-sm text-[#1B365D] hover:underline inline-flex items-center gap-1">
                                    <ArrowLeft className="w-4 h-4" /> Retour à la connexion
                                </Link>
                            </div>
                        ) : done ? (
                            <div className="text-center py-6 space-y-4" data-testid="reset-success">
                                <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto" />
                                <p className="text-sm text-[#1B365D]">Votre mot de passe a été mis à jour avec succès. Redirection en cours…</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="new-password" className="text-gray-700">Nouveau mot de passe</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <Input
                                            id="new-password"
                                            type={showPwd ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            placeholder="Minimum 8 caractères"
                                            className="pl-10 pr-10 h-12"
                                            data-testid="reset-password-input"
                                        />
                                        <button type="button" onClick={() => setShowPwd(!showPwd)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            data-testid="reset-toggle-password">
                                            {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    {tooShort && <p className="text-xs text-red-500">8 caractères minimum.</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password" className="text-gray-700">Confirmer le mot de passe</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <Input
                                            id="confirm-password"
                                            type={showPwd ? 'text' : 'password'}
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            required
                                            placeholder="Retapez votre mot de passe"
                                            className="pl-10 h-12"
                                            data-testid="reset-confirm-input"
                                        />
                                    </div>
                                    {mismatch && <p className="text-xs text-red-500">Les deux mots de passe ne correspondent pas.</p>}
                                </div>

                                <Button type="submit"
                                    disabled={!canSubmit}
                                    className="w-full h-12 bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                                    data-testid="reset-submit">
                                    {submitting ? 'Enregistrement…' : 'Mettre à jour le mot de passe'}
                                </Button>

                                <div className="text-center pt-1">
                                    <Link to="/login" className="text-sm text-gray-500 hover:text-[#1B365D] inline-flex items-center gap-1">
                                        <ArrowLeft className="w-4 h-4" /> Retour à la connexion
                                    </Link>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
                <p className="mt-6 text-center text-sm text-gray-500">© 2024 Luxy Move. Tous droits réservés.</p>
            </div>
        </div>
    );
}
